import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Search, 
  X, 
  User, 
  Users, 
  Trophy, 
  Clock, 
  TrendingUp,
  Star,
  Award,
  Target,
} from '../../components/common/Icons';
import { useSearch } from '../../context/SearchContext';
import { useNavigate } from 'react-router-dom';
import Link from './Link';
import UserAvatar from './UserAvatar';
import TournamentLogo from './TournamentLogo';
import TeamLogo from './TeamLogo';

const PLATFORM_ICON = { steam: '/steam.ico', psn: '/psn.ico', playstation: '/psn.ico', xbox: '/xbox.ico' };

const SearchModal = ({ isOpen, onClose, initialQuery = '' }) => {
  const navigate = useNavigate();
  const { 
    query, 
    results, 
    suggestions, 
    loading, 
    hasResults, 
    isEmpty,
    debouncedSearch,
    debouncedSuggestions,
    clearSearch,
    getRecentSearches,
    getPopularSearches
  } = useSearch();

  const [localQuery, setLocalQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSelectedIndex(-1);
      setShowSuggestions(false);
      const seed = (initialQuery || '').trim();
      setLocalQuery(seed);
      if (seed.length >= 2) {
        debouncedSearch(seed);
      } else {
        clearSearch();
      }
    }
  }, [isOpen, initialQuery, clearSearch, debouncedSearch]);

  // Handle click outside and prevent body scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleResultClick = useCallback((result) => {
    onClose();
    switch (result.type) {
      case 'player':
        navigate(`/profile/${result.id}`);
        break;
      case 'team':
        navigate(`/teams/${result.id}`);
        break;
      case 'tournament':
        navigate(`/tournaments/${result.id}`);
        break;
      default:
        console.warn('Unknown result type:', result.type);
    }
  }, [onClose, navigate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        const totalItems = (hasResults ? results.combined.length : 0) + 
                          (showSuggestions ? suggestions.length : 0) +
                          (getRecentSearches().length + getPopularSearches().length);
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
      } else if (event.key === 'Enter' && selectedIndex >= 0) {
        event.preventDefault();
        if (hasResults && selectedIndex < results.combined.length) {
          handleResultClick(results.combined[selectedIndex]);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, selectedIndex, hasResults, results, showSuggestions, suggestions, getRecentSearches, getPopularSearches, handleResultClick]);


  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocalQuery(value);
    setSelectedIndex(-1);
    
    if (value.trim().length >= 2) {
      debouncedSearch(value);
      setShowSuggestions(false);
    } else if (value.trim().length === 1) {
      debouncedSuggestions(value);
      setShowSuggestions(true);
    } else {
      clearSearch();
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setLocalQuery(suggestion.text);
    debouncedSearch(suggestion.text);
    setShowSuggestions(false);
  };

  const handleHistoryClick = (historyQuery) => {
    setLocalQuery(historyQuery);
    debouncedSearch(historyQuery);
    setShowSuggestions(false);
  };

  const formatPlayerStats = (stats) => {
    if (!stats) return null;
    return (
      <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center space-x-1">
          <Target className="w-3 h-3" />
          <span>{stats.goals} goals</span>
        </span>
        <span className="flex items-center space-x-1">
          <Star className="w-3 h-3" />
          <span>{stats.assists} assists</span>
        </span>
        <span>{stats.matches} matches</span>
      </div>
    );
  };

  const formatTeamStats = (stats) => {
    if (!stats) return null;
    return (
      <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center space-x-1">
          <Award className="w-3 h-3" />
          <span>{stats.wins}W-{stats.losses}L</span>
        </span>
        <span className="flex items-center space-x-1">
          <Trophy className="w-3 h-3" />
          <span>{stats.tournaments} tournaments</span>
        </span>
      </div>
    );
  };

  const formatTournamentStats = (stats) => {
    if (!stats) return null;
    return (
      <div className="flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center space-x-1">
          <Users className="w-3 h-3" />
          <span>{stats.registeredTeams}/{stats.maxTeams} teams</span>
        </span>
        <span className="flex items-center space-x-1">
          <Award className="w-3 h-3" />
          <span>{stats.prize}</span>
        </span>
      </div>
    );
  };

  const recentSearches = getRecentSearches();
  const popularSearches = getPopularSearches();

  if (!isOpen) return null;

  const sectionHeader = (Icon, label, count) => (
    <h3 className="flex items-center gap-2 mb-2.5 text-[#23F8B5]">
      <Icon className="w-4 h-4" />
      <span className="font-bebas text-lg tracking-wide text-white">{label}</span>
      {count != null && <span className="text-sm font-semibold text-white/40">({count})</span>}
    </h3>
  );

  const platformBadge = (platform) => {
    if (!platform) return null;
    const key = platform.toLowerCase();
    const icon = PLATFORM_ICON[key];
    if (icon) {
      return <img src={icon} alt={platform} title={platform} className="flex-shrink-0 w-5 h-5 opacity-90" />;
    }
    return <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide bg-[#31C590]/15 text-[#23F8B5] border border-[#31C590]/25">{platform.toUpperCase()}</span>;
  };
  const rowClass = "block w-full text-left px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.06] hover:border-[#31C590]/40 transition-all duration-200 no-underline text-inherit";

  return createPortal(
    <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm flex items-start justify-center p-4 sm:pt-[8vh]">
      <div className="w-full max-w-3xl max-h-[86vh] flex flex-col">
        <div
          ref={modalRef}
          className="relative flex flex-col min-h-0 bg-[#12151e] border border-white/10 rounded-2xl shadow-[0_20px_80px_rgba(0,0,0,0.6),0_0_0_1px_rgba(49,197,144,0.08)] overflow-hidden"
        >
        {/* Search Header — matches the homepage search box */}
        <div className="p-3.5 border-b border-white/[0.07] bg-gradient-to-b from-[#31C590]/[0.06] to-transparent">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 flex items-center rounded-2xl border border-white/10 bg-zinc-900/70 backdrop-blur-md shadow-2xl focus-within:border-[#31C590]/60 focus-within:shadow-[0_0_30px_rgba(49,197,144,0.25)] transition-all duration-300">
              <Search className="flex-shrink-0 w-5 h-5 ml-4 text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                value={localQuery}
                onChange={handleInputChange}
                placeholder="Search players, teams, tournaments..."
                className="flex-1 min-w-0 bg-transparent px-3 py-3 text-base text-white placeholder-gray-500 focus:outline-none"
              />
              {loading && (
                <div className="flex-shrink-0 mr-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#31C590]/30 border-t-[#23F8B5]"></div>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/10"
              aria-label="Close search"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Content */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
          {loading && (
            <div className="p-10 text-center">
              <div className="animate-spin rounded-full h-9 w-9 border-2 border-[#31C590]/30 border-t-[#23F8B5] mx-auto mb-4"></div>
              <p className="text-gray-400">Searching…</p>
            </div>
          )}

          {showSuggestions && suggestions.length > 0 && (
            <div className="p-4">
              {sectionHeader(TrendingUp, 'Suggestions')}
              <div className="space-y-1">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/[0.06] transition-colors flex items-center gap-3"
                  >
                    <span className="text-lg">{suggestion.icon}</span>
                    <span className="text-gray-200">{suggestion.text}</span>
                    <span className="text-xs text-gray-500 capitalize">({suggestion.type})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasResults && !loading && (
            <div className="p-4">
              <div className="mb-3 text-sm text-gray-400">
                Found <span className="text-white font-semibold">{results.total}</span> result{results.total !== 1 ? 's' : ''} for “<span className="text-[#23F8B5]">{query}</span>”
              </div>

              {results.players.length > 0 && (
                <div className="mb-4">
                  {sectionHeader(User, 'Players', results.players.length)}
                  <div className="space-y-1.5">
                    {results.players.map((player) => (
                      <Link
                        key={player.id}
                        to={`/profile/${player.id}`}
                        onClick={() => onClose()}
                        className={rowClass}
                      >
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            user={{
                              id: player.id,
                              username: player.meta.username,
                              displayName: player.meta.displayName,
                              avatarUrl: player.avatar
                            }}
                            size="w-8 h-8"
                            textSize="text-xs"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate leading-tight">{player.title}</div>
                            <div className="text-[13px] text-gray-400 truncate">{player.subtitle}</div>
                            {formatPlayerStats(player.stats)}
                          </div>
                          {platformBadge(player.platform)}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {results.teams.length > 0 && (
                <div className="mb-4">
                  {sectionHeader(Users, 'Teams', results.teams.length)}
                  <div className="space-y-1.5">
                    {results.teams.map((team) => (
                      <Link
                        key={team.id}
                        to={`/teams/${team.id}`}
                        onClick={() => onClose()}
                        className={rowClass}
                      >
                        <div className="flex items-center gap-3">
                          <TeamLogo
                            team={{
                              id: team.id,
                              name: team.title,
                              logo: team.avatar
                            }}
                            size="w-8 h-8"
                            textSize="text-xs"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate">{team.title}</div>
                            <div className="text-sm text-gray-400 truncate">{team.subtitle}</div>
                            {formatTeamStats(team.stats)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {results.tournaments.length > 0 && (
                <div className="mb-2">
                  {sectionHeader(Trophy, 'Tournaments', results.tournaments.length)}
                  <div className="space-y-1.5">
                    {results.tournaments.map((tournament) => (
                      <Link
                        key={tournament.id}
                        to={`/tournaments/${tournament.id}`}
                        onClick={() => onClose()}
                        className={rowClass}
                      >
                        <div className="flex items-center gap-3">
                          <TournamentLogo
                            tournament={tournament}
                            size="w-8 h-8"
                            textSize="text-base"
                            showBorder={true}
                            borderColor="border-white/10"
                            rounded="rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate">{tournament.title}</div>
                            <div className="text-sm text-gray-400 truncate">{tournament.subtitle}</div>
                            {formatTournamentStats(tournament.stats)}
                          </div>
                          <div className={`flex-shrink-0 px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide border ${
                            tournament.meta.status === 'open' ? 'bg-[#31C590]/15 text-[#23F8B5] border-[#31C590]/25' :
                            tournament.meta.status === 'in_progress' ? 'bg-blue-500/15 text-blue-300 border-blue-500/25' :
                            tournament.meta.status === 'completed' ? 'bg-white/10 text-gray-300 border-white/15' :
                            'bg-red-500/15 text-red-300 border-red-500/25'
                          }`}>
                            {tournament.meta.statusDisplay}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {isEmpty && !loading && (
            <div className="p-12 text-center">
              <Search className="w-12 h-12 text-white/15 mx-auto mb-4" />
              <h3 className="font-bebas text-2xl tracking-wide text-white mb-1">No results found</h3>
              <p className="text-gray-400">
                Try searching for different keywords or check your spelling.
              </p>
            </div>
          )}

          {!loading && !hasResults && !isEmpty && localQuery.trim().length < 2 && (
            <div className="p-4">
              {recentSearches.length > 0 && (
                <div className="mb-4">
                  {sectionHeader(Clock, 'Recent Searches')}
                  <div className="space-y-1">
                    {recentSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleHistoryClick(search)}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/[0.06] transition-colors flex items-center gap-3"
                      >
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-200">{search}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {popularSearches.length > 0 && (
                <div>
                  {sectionHeader(TrendingUp, 'Popular Searches')}
                  <div className="space-y-1">
                    {popularSearches.map((search, index) => (
                      <button
                        key={index}
                        onClick={() => handleHistoryClick(search)}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/[0.06] transition-colors flex items-center gap-3"
                      >
                        <TrendingUp className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-200">{search}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {recentSearches.length === 0 && popularSearches.length === 0 && (
                <div className="text-center py-14">
                  <div className="mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center bg-[#31C590]/10 border border-[#31C590]/20">
                    <Search className="w-7 h-7 text-[#23F8B5]" />
                  </div>
                  <h3 className="font-bebas text-2xl tracking-wide text-white mb-1">Start searching</h3>
                  <p className="text-gray-400">
                    Find players, teams, and tournaments across the platform.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;
